import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fadeIn } from '../variants';
import { sendMessageToFirestore } from '../firestoreService';
import formRoseauImage from '../img/orangeBrownRoseaux.png';
import { CardBody, CardContainer, CardItem } from "./3d-card.tsx";
import RaindropAnimation from './RaindropAnimation.js';

// Fonction de validation pour mon formulaire
const validateContactForm = (nom, email, message) => {
    let errors = {};
    if (!nom.trim()) {
        errors.nom = "Le nom est requis.";
    }
    if (!email) {
        errors.email = "Le courriel est requis.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
        errors.email = "L'adresse courriel est invalide.";
    }
    if (!message.trim()) {
        errors.message = "Le message ne peut pas être vide.";
    }
    return errors;
};


const Contact = () => {

    // États locaux pour les champs du formulaire
    const [nom, setNom] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState({});

    // Hook d'animaion de gouttes de pluie au submit du formulaire
    const [shouldDisplayRaindrop, setShouldDisplayRaindrop] = useState(false);

    // Fonction pour gérer la soumission du formulaire
    const handleSubmit = (event) => {
        event.preventDefault();
        const formErrors = validateContactForm(nom, email, message);
        if (Object.keys(formErrors).length === 0) {
            sendMessageToFirestore(nom, email, message);
            setNom('');
            setEmail('');
            setMessage('');
            setShouldDisplayRaindrop(true); // Déclencher l'animation après l'envoi réussi
        } else {
            setErrors(formErrors);
        }
    };

    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 500);
        };

        // Vérifier immédiatement et ensuite à chaque redimensionnement
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);

        // Nettoyage de l'écouteur d'événements
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Utilisez une div normale au lieu de motion.div si isMobile est true
    const ResponsiveDiv = isMobile ? 'div' : motion.div;

    const [hover, setHover] = useState(false);

    const [formFields, setFormFields] = useState({
        nom: '',
        email: '',
        message: '',
      });
      const [focus, setFocus] = useState({
        nom: false,
        email: false,
        message: false,
      });
    
      // Gestion des changements de focus
      const handleFocus = (field) => setFocus({ ...focus, [field]: true });
      const handleBlur = (field) => setFocus({ ...focus, [field]: false });
    
      // Gestion des changements dans les champs de saisie
      const handleChange = (e) => {
        const { name, value } = e.target;
        setFormFields({ ...formFields, [name]: value });
      };

    return (
        <section className="section bg-site1 bg-cover" id='contact'>
            <div className='container mx-auto'>
                <div className='flex flex-col lg:flex-row'>
                    <ResponsiveDiv className='flex-1 flex justify-start items-center'
                        variants={fadeIn("right", 0.3)}
                        initial="hidden"
                        whileInView={"show"}
                        viewport={{ once: false, amount: 0.7 }}
                    >
                        <div>
                            <h4 className='text-xl uppercase font-mediun mb-6 tracking-wide' style={{ color: '#F08927' }}>Contactez-moi</h4>
                            <h2 className='text-4xl lg:text-6xl leading-none font-primary'
                                style={{
                                    backgroundColor: 'rgba(0,0,0, 0.24)',
                                    padding: '1rem',
                                    boxShadow: '0px 4.4218px 107.23px rgba(255, 255, 255, 0.51)',
                                    backdropFilter: 'blur(4px)',
                                    WebkitBackdropFilter: 'blur(4px)',
                                    textShadow: '0px 4.4218px 107.23px rgba(249, 115, 22, 0.51)',
                                    borderRadius: '20px'
                                }}>
                                Travaillons <br /> ensemble
                            </h2>
                        </div>
                    </ResponsiveDiv>
                    <ResponsiveDiv className='flex-1 lg:mt-0 mt-8 border rounded-2xl flex flex-col gap-y-5 pb-24 p-6 items-start'
                        variants={fadeIn("left", 0.3)}
                        initial="hidden"
                        whileInView={"show"}
                        viewport={{ once: false, amount: 0.7 }}
                        onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
                        style={{
                            backgroundImage: hover ? `url(${formRoseauImage})` : 'none',
                            backgroundSize: 'calc(100% + 100rem) calc(100% + 120rem)',
                            backgroundPosition: 'center center',
                            backgroundRepeat: 'no-repeat',
                            position: 'relative',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        <form onSubmit={handleSubmit} className='mx-auto'>
                            <CardContainer>
                                <CardBody>
                                    <CardItem>
                                        <input
                                            name="nom"
                                            type="text"
                                            placeholder={focus.nom ? '' : 'Votre nom'}
                                            value={formFields.nom}
                                            onChange={handleChange}
                                            onFocus={() => handleFocus('nom')}
                                            onBlur={() => handleBlur('nom')}
                                            className="bg-transparent border-b-2 border-orange-500 py-3 outline-none transition-all duration-300"
                                            id='nomForm'
                                            style={{
                                                borderColor: '#F08927',
                                                borderWidth: isMobile ? '1px' : '2px',
                                                paddingLeft: isMobile ? '1rem' : '1rem', 
                                                paddingRight: isMobile ? '1rem' : '0', 
                                                marginTop: '1rem',
                                                width: isMobile ? 'auto' : '100%', 
                                                backgroundColor: focus.nom ? 'rgba(29, 19, 21, 1.0)' : 'rgba(247, 118, 31, 0.1)',
                                                placeholderTextColor: '#F7761F',
                                                caretColor: focus.nom ? '#F08927' : 'inherit',
                                            }}
                                        />
                                        {errors.nom && <p className='text-red-500 text-xs'>{errors.nom}</p>}
                                        <input
                                            name="email"
                                            type="email"
                                            placeholder={focus.email ? '' : 'Votre courriel'}
                                            value={formFields.email}
                                            onChange={handleChange}
                                            onFocus={() => handleFocus('email')}
                                            onBlur={() => handleBlur('email')}
                                            className="bg-transparent border-b-2 border-orange-500 py-3 outline-none transition-all duration-300"
                                            id='emailForm'
                                            style={{
                                                borderColor: '#F08927',
                                                borderWidth: isMobile ? '1px' : '2px',
                                                paddingLeft: isMobile ? '1rem' : '1rem', 
                                                paddingRight: isMobile ? '1rem' : '0', 
                                                marginTop: '1rem',
                                                width: isMobile ? 'auto' : '100%', 
                                                backgroundColor: focus.email ? 'rgba(29, 19, 21, 1.0)' : 'rgba(247, 118, 31, 0.1)',
                                                placeholderTextColor: '#F7761F',
                                                caretColor: focus.email ? '#F08927' : 'inherit',
                                            }}
                                        />
                                        {errors.email && <p className='text-red-500 text-xs'>{errors.email}</p>}

                                        <textarea
                                            name="message"
                                            rows="5"
                                            placeholder={focus.message ? '' : 'Votre message'}
                                            value={formFields.message}
                                            onChange={handleChange}
                                            onFocus={() => handleFocus('message')}
                                            onBlur={() => handleBlur('message')}
                                            className="bg-transparent border-b-2 border-orange-500 py-3 outline-none transition-all duration-300"
                                            style={{
                                                borderColor: '#F08927',
                                                borderWidth: isMobile ? '1px' : '2px',
                                                paddingLeft: isMobile ? '1rem' : '1rem',
                                                paddingRight: isMobile ? '1rem' : '0',
                                                marginTop: '1rem',
                                                width: isMobile ? 'auto' : '100%',
                                                backgroundColor: focus.message ? 'rgba(29, 19, 21, 1.0)' : 'rgba(247, 118, 31, 0.1)',
                                                placeholderTextColor: '#F7761F',
                                                caretColor: focus.message ? '#F08927' : 'inherit',
                                            }}
                                        />
                                        {errors.message && <p className='text-red-500 text-xs'>{errors.message}</p>}
                                        <button
                                            type="submit"
                                            className="btnBase4 mx-auto btn-lg lg:btn-lg xs:btn-sm xxs:btn-sm xxxs:btn-xs xxxxs:btn-xs xxxxxs:btn-xxs xxxxxs:p-2"
                                            style={{ borderColor: '#F08927', marginTop: '5rem', textAlign: 'center' }}
                                        >
                                            Envoyez-moi votre message
                                        </button>
                                    </CardItem>
                                </CardBody>
                            </CardContainer>
                        </form>
                        {shouldDisplayRaindrop && <RaindropAnimation />}
                    </ResponsiveDiv>
                </div>
                <ResponsiveDiv className='flex-4 flex justify-end items-center droitAuteur'
                    variants={fadeIn("right", 0.3)}
                    initial="hidden"
                    whileInView={"show"}
                    viewport={{ once: false, amount: 0.7 }}
                    style={{ display: 'inline-block', color: '#F7761F', margin: '0 auto', textAlign: 'center', marginTop: 'clamp(4rem, 6.5vw, 15rem)' }}
                >
                    2024 Porfolio Philippe Bolduc. Tous droits réservés<span>&nbsp;©</span>
                </ResponsiveDiv>
            </div>
        </section>
    )
};

export default Contact;



